import _axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { RequestStatus } from 'types'
import env from '../env'

interface SafeResponse<T> {
    data?: T
    error?: string | string[] | Record<string, unknown>
}

export interface RequestState<T> {
    status: RequestStatus
    data: undefined | null | T
    error?: string | string[] | Record<string, unknown>
}

const withUrlCheck = (fn) => (url: string, data?: any, config?: AxiosRequestConfig) => {
    if (env.isProduction) {
        return fn(url, data, config)
    }
    const path = url.split('?')[0]
    if (!path.endsWith('/')) {
        console.error(`Path ${path} does not end in slash. Django might return 500 for this request`)
    }
    if (url.startsWith('api/') || url.startsWith('/api/')) {
        console.error(`URL ${url} starts with /api/, this should be omitted because its included in the base url`)
    }
    return fn(url, data, config)
}

const withParseResponse = (fn) => async <T>(...args): Promise<T> => {
    const resp: AxiosResponse = await fn(...args)
    if (resp && resp.data) {
        return resp.data
    }
    return resp as unknown as Promise<T>
}

export const getPlainAxios = () => _axios.create({
    baseURL: env.apiUrl,
})

export const axios = getPlainAxios()
const post = withParseResponse(withUrlCheck(axios.post))
const get = withParseResponse(withUrlCheck(axios.get))
const put = withParseResponse(withUrlCheck(axios.put))
const patch = withParseResponse(withUrlCheck(axios.patch))
const del = withParseResponse(withUrlCheck(axios.delete))

const safeResponse = (fn) => async <T=any>(
    url: string, data?: any, config?: AxiosRequestConfig & { disableAuth?: boolean },
): Promise<SafeResponse<T>> => {
    try {
        const resp = await fn(url, data, config)
        return { data: resp }
    } catch (error) {
        if (error.response) {
            // Request made and server responded
            return error.response.data?.detail
                ? { error: error.response.data.detail }
                : { error: error.response.data }
        } if (error.request) {
            // The request was made but no response was received
            return { error: 'Something went wrong' }
        }
        // Something happened in setting up the request that triggered an Error
        return { error: error.message }
    }
}

export default {
    // In thunks you need the request to raise if something goes wrong
    // so use post / get
    post,
    get,
    put,
    patch,
    delete: del,
    // Outside of thunks you don't want the request to raise,
    // so use safePost / safeGet
    safePost: safeResponse(post),
    safeGet: safeResponse(get),
    safePut: safeResponse(put),
    safePatch: safeResponse(patch),
    safeDelete: safeResponse(del),
}
